import type { ContractContactAlertType } from "../types";

/** What a destination is: a number to call or text, or an address to email. */
export type DestinationKind = "phone" | "email";

/** SMS and calls go to a phone; an email goes to an address. */
export const destinationKindOf = (alertType: ContractContactAlertType): DestinationKind =>
  alertType === "email" ? "email" : "phone";

/**
 * A destination in the shape the backend stores and compares it in
 * (`ContactChannel::normalizeValue`): digits only for a phone, trimmed and
 * lowercased for an email. The drawer shows `514-300-5432` while the server
 * holds `5143005432`, so anything that matches the two compares this.
 */
export const normalizeDestination = (kind: DestinationKind, value: string | null | undefined): string => {
  const raw = value ?? "";

  return kind === "email" ? raw.trim().toLowerCase() : raw.replace(/\D+/g, "");
};

/** One key per destination: its kind plus its normalized value. */
export const destinationKey = (kind: DestinationKind, value: string | null | undefined) =>
  `${kind}|${normalizeDestination(kind, value)}`;

/**
 * A ten-digit number dashed the way the design writes it (`514-300-5432`).
 * Display only — anything else (an extension, a leading 1) is shown as stored.
 */
export const formatPhone = (value: string): string => {
  const digits = value.replace(/\D+/g, "");

  if (digits.length !== 10) {
    return value;
  }

  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
};
